import React, { useState } from 'react';
import { ScanFace, Plus, Check, X, Loader2, RefreshCw, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';

const split = (s) => (Array.isArray(s) ? s : (s || '').split(',')).map((x) => String(x).trim()).filter(Boolean);

// Admin: detect faces, suggest member names, confirm each into the People field with one click.
export default function FaceSuggestions({ photo, onPhotoChange }) {
  const { adminFaceSuggest, adminPhotoPeople } = useAuth();
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState('');
  const people = split(photo.people);
  const has = (n) => people.some((p) => p.toLowerCase() === n.toLowerCase());
  const fs = photo.face_suggestions;
  const memberNames = (fs?.members || []).map((m) => m.name);
  const rawNames = (fs?.names || []).filter((n) => !memberNames.some((m) => m.toLowerCase().includes(n.toLowerCase()) || n.toLowerCase().includes(m.toLowerCase())));
  const pending = [...memberNames, ...rawNames].filter((n) => !has(n));

  const detect = async () => {
    setBusy(true);
    try { const r = await adminFaceSuggest(photo.id); onPhotoChange(r); toast.success(r.face_suggestions?.people_count ? `Detected ${r.face_suggestions.people_count} ${r.face_suggestions.people_count === 1 ? 'person' : 'people'}.` : 'No faces detected.'); }
    catch (e) { toast.error(formatApiError(e, 'Face detection failed.')); }
    finally { setBusy(false); }
  };
  const change = async (body, label) => {
    setAdding(label);
    try { const r = await adminPhotoPeople(photo.id, body); onPhotoChange(r); }
    catch (e) { toast.error(formatApiError(e, 'Could not update People.')); }
    finally { setAdding(''); }
  };

  return (
    <div className="rounded p-2.5 space-y-2" style={{ background: 'rgba(201,161,59,0.10)' }} data-testid="face-suggestions">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-title inline-flex items-center gap-1" style={{ color: 'var(--navy)' }}><ScanFace size={13} /> People in this photo</span>
        <button onClick={detect} disabled={busy} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded font-title disabled:opacity-60" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="face-detect-btn">
          {busy ? <Loader2 size={11} className="animate-spin" /> : fs ? <RefreshCw size={11} /> : <ScanFace size={11} />} {busy ? 'Detecting…' : fs ? 'Re-detect' : 'Detect faces & suggest names'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1" data-testid="face-people">
        {people.length === 0 && <span className="text-[11px] italic" style={{ color: 'var(--olive)' }}>No one named yet.</span>}
        {people.map((n) => (
          <span key={n} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid={`face-person-${n}`}>
            <Check size={10} /> {n}
            <button onClick={() => change({ remove: [n] }, n)} title={`Remove ${n}`} data-testid={`face-remove-${n}`}><X size={10} /></button>
          </span>
        ))}
      </div>
      {fs && (
        <div className="space-y-1.5" data-testid="face-suggestion-block">
          <div className="text-[11px]" style={{ color: 'var(--olive)' }} data-testid="face-count">
            Detected <b>{fs.people_count}</b> {fs.people_count === 1 ? 'person' : 'people'}{fs.at ? ` · ${new Date(fs.at).toLocaleString()}` : ''}
            {pending.length === 0 && (fs.members?.length || fs.names?.length) ? ' · all suggestions confirmed' : ''}
            {!fs.members?.length && !fs.names?.length ? ' · no names could be suggested' : ''}
          </div>
          {pending.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {memberNames.filter((n) => !has(n)).map((n) => (
                <button key={n} onClick={() => change({ add: [n] }, n)} disabled={!!adding} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border disabled:opacity-60" style={{ borderColor: 'var(--maroon)', color: 'var(--maroon)', background: 'white' }} title="Known member — click to add" data-testid={`face-suggest-${n}`}>
                  {adding === n ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />} {n}
                </button>
              ))}
              {rawNames.filter((n) => !has(n)).map((n) => (
                <button key={n} onClick={() => change({ add: [n] }, n)} disabled={!!adding} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-dashed disabled:opacity-60" style={{ borderColor: 'var(--olive)', color: 'var(--olive)', background: 'white' }} title="Name read from the photo (not a known member)" data-testid={`face-suggest-${n}`}>
                  {adding === n ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />} {n}
                </button>
              ))}
              {pending.length > 1 && (
                <button onClick={() => change({ add: pending }, '*')} disabled={!!adding} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-title disabled:opacity-60" style={{ background: 'var(--gold)', color: '#000' }} data-testid="face-add-all">
                  <UserPlus size={10} /> Add all {pending.length}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
